// src/components/AdminDashboard/sections/AddCourtContainer.jsx
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import AddCourtSection from './AddCourtSection';
import courtService from '../../../../services/courtService';

const AddCourtContainer = () => {
  // basic court fields
  const [newCourtName,    setNewCourtName]    = useState('');
  const [newCourtHlsUrl,  setNewCourtHlsUrl]  = useState('');
  const [ownerUsername,   setOwnerUsername]   = useState('');

  // cameras: we'll accumulate N entries; 
  // the Section only edits the "current" name/url pair
  const [newCourtCameraName, setNewCourtCameraName] = useState('');
  const [newCourtCameraUrl,  setNewCourtCameraUrl]  = useState('');
  const [cameraUrls, setCameraUrls] = useState({}); 
  // e.g. { "Cam 1": "rtsp://...", "Cam 2": "..." }

  // court sessions
  const [sessions, setSessions] = useState([]); // array of { sessionName, dayOfWeek, startTime, endTime, active }
  const [tempSessionName, setTempSessionName] = useState('');
  const [tempDayOfWeek,    setTempDayOfWeek]    = useState('MONDAY');
  const [tempStartTime,    setTempStartTime]    = useState('08:00:00');
  const [tempEndTime,      setTempEndTime]      = useState('10:00:00');
  const [tempActive,       setTempActive]       = useState(true);

  const handleAddSession = () => {
    if (!tempSessionName) {
      toast.error('Session name is required');
      return;
    }
    
    setSessions([
      ...sessions,
      { 
        sessionName: tempSessionName,
        dayOfWeek: tempDayOfWeek,
        startTime: tempStartTime,
        endTime: tempEndTime,
        active: tempActive
      }
    ]);
    setTempSessionName('');
  };

  const handleRemoveSession = (idx) => {
    setSessions(sessions.filter((_, i) => i !== idx));
  };

  const handleAddCamera = () => {
    if (!newCourtCameraName || !newCourtCameraUrl) {
      toast.error('Both camera name and URL are required');
      return;
    }

    // Check for duplicate camera names
    if (cameraUrls[newCourtCameraName]) {
      toast.error('A camera with this name already exists');
      return;
    }

    // Validate RTSP URL format
    if (!newCourtCameraUrl.startsWith('rtsp://')) {
      toast.error('Camera URL must be a valid RTSP URL (starting with rtsp://)');
      return;
    }

    setCameraUrls({
      ...cameraUrls,
      [newCourtCameraName]: newCourtCameraUrl
    });
    
    // Clear the input fields
    setNewCourtCameraName('');
    setNewCourtCameraUrl('');
    
    toast.success('Camera added successfully');
  };

  const handleRemoveCamera = (cameraName) => {
    const newCameraUrls = { ...cameraUrls };
    delete newCameraUrls[cameraName];
    setCameraUrls(newCameraUrls);
    toast.success('Camera removed');
  };

  const handleAddCourt = async (e) => {
    e.preventDefault();

    if (!ownerUsername) {
      toast.error('Owner username is required');
      return;
    }

    const courtDto = {
      courtName: newCourtName,
      hlsUrl: newCourtHlsUrl,
      cameras: Object.entries(cameraUrls).map(([name, url]) => ({
        cameraName: name,
        cameraUrl: url,
        active: true
      })),
      sessions: sessions.map(session => ({
        sessionName: session.sessionName,
        dayOfWeek: session.dayOfWeek,
        startTime: session.startTime,
        endTime: session.endTime,
        active: session.active
      }))
    };

    try {
      const createdCourt = await courtService.addCourt(courtDto, ownerUsername);
      toast.success(`Court "${createdCourt.courtName}" created!`);
      // reset everything
      setNewCourtName('');
      setNewCourtHlsUrl('');
      setOwnerUsername('');
      setCameraUrls({});
      setSessions([]);
    } catch (err) {
      toast.error(err.message || 'Failed to create court');
    }
  };

  return (
    <AddCourtSection
      newCourtName={newCourtName}
      setNewCourtName={setNewCourtName}
      newCourtHlsUrl={newCourtHlsUrl}
      setNewCourtHlsUrl={setNewCourtHlsUrl}
      ownerUsername={ownerUsername}
      setOwnerUsername={setOwnerUsername}

      newCourtCameraName={newCourtCameraName}
      setNewCourtCameraName={setNewCourtCameraName}
      newCourtCameraUrl={newCourtCameraUrl}
      setNewCourtCameraUrl={setNewCourtCameraUrl}

      sessions={sessions}
      tempSessionName={tempSessionName}
      setTempSessionName={setTempSessionName}
      tempDayOfWeek={tempDayOfWeek}
      setTempDayOfWeek={setTempDayOfWeek}
      tempStartTime={tempStartTime}
      setTempStartTime={setTempStartTime}
      tempEndTime={tempEndTime}
      setTempEndTime={setTempEndTime}
      tempActive={tempActive}
      setTempActive={setTempActive}

      handleAddSession={handleAddSession}
      handleRemoveSession={handleRemoveSession}
      handleAddCourt={handleAddCourt}
      handleAddCamera={handleAddCamera}
      handleRemoveCamera={handleRemoveCamera}
      cameraUrls={cameraUrls || {}}
    />
  );
};

export default AddCourtContainer;
